import React, { useState, useEffect, createContext, useContext } from "react";
import API_URL from "../config/api";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem("vm_token") || localStorage.getItem("vm_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Try sessionStorage first (more secure), fallback to localStorage for compatibility
      const storedUser = sessionStorage.getItem("vm_user") || localStorage.getItem("vm_user");
      const storedToken = sessionStorage.getItem("vm_token") || localStorage.getItem("vm_token");

      if (storedToken) {
        if (storedUser) {
          // User data exists
          try {
            setUser(JSON.parse(storedUser));
          } catch (parseError) {
            console.error("Failed to parse stored user data:", parseError);
            sessionStorage.removeItem("vm_user");
            localStorage.removeItem("vm_user");
          }
        } else {
          // Token exists but no user data - fetch from API (with 5s timeout)
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 5000);
          try {
            const response = await fetch(`${API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${storedToken}` },
              signal: controller.signal,
            });

            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
              sessionStorage.setItem("vm_user", JSON.stringify(userData));
            } else {
              // Token invalid - clear everything
              sessionStorage.removeItem("vm_token");
              sessionStorage.removeItem("vm_user");
              localStorage.removeItem("vm_token");
              localStorage.removeItem("vm_user");
              setToken(null);
            }
          } catch (error) {
            if (error.name !== "AbortError") {
              console.error("Failed to fetch user data:", error);
            }
            // Clear stale token on timeout/network error so site doesn't hang
            sessionStorage.removeItem("vm_token");
            localStorage.removeItem("vm_token");
            setToken(null);
          } finally {
            clearTimeout(timeout);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = (userData, accessToken, rememberMe = false) => {
    setUser(userData);
    setToken(accessToken);

    // Use sessionStorage by default (cleared on browser close)
    sessionStorage.setItem("vm_token", accessToken);
    sessionStorage.setItem("vm_user", JSON.stringify(userData));

    // Only persist to localStorage if remember me is checked
    if (rememberMe) {
      localStorage.setItem("vm_token", accessToken);
      localStorage.setItem("vm_user", JSON.stringify(userData));
    } else {
      // Clear localStorage to ensure we're only using sessionStorage
      localStorage.removeItem("vm_token");
      localStorage.removeItem("vm_user");
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    sessionStorage.removeItem("vm_token");
    sessionStorage.removeItem("vm_user");
    localStorage.removeItem("vm_token");
    localStorage.removeItem("vm_user");
  };

  const updateUser = (userData) => {
    setUser(userData);
    sessionStorage.setItem("vm_user", JSON.stringify(userData));
    // Also update localStorage if it exists there
    if (localStorage.getItem("vm_user")) {
      localStorage.setItem("vm_user", JSON.stringify(userData));
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
